import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calculator, DollarSign, Clock, TrendingUp } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";

const projectTypes = [
  { label: "MVP / Prototype", baseWeeks: 8, baseCost: 40000 },
  { label: "Full Product", baseWeeks: 14, baseCost: 80000 },
  { label: "Enterprise Platform", baseWeeks: 24, baseCost: 160000 },
  { label: "Legacy Migration", baseWeeks: 20, baseCost: 120000 },
];

const complexityLevels = [
  { label: "Standard", multiplier: 1.0 },
  { label: "Moderate", multiplier: 1.4 },
  { label: "Complex (FinTech/Trading)", multiplier: 1.8 },
];

export default function ROICalculatorPage() {
  const [projectType, setProjectType] = useState(0);
  const [complexity, setComplexity] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(50000);
  const [currentDevCost, setCurrentDevCost] = useState(30000);
  const [showResults, setShowResults] = useState(false);

  const project = projectTypes[projectType];
  const comp = complexityLevels[complexity];

  const venuryCost = Math.round(project.baseCost * comp.multiplier);
  const venuryWeeks = Math.round(project.baseWeeks * (comp.multiplier * 0.7));
  const traditionalWeeks = Math.round(project.baseWeeks * comp.multiplier * 2.5);
  const traditionalCost = Math.round(venuryCost * 2.2);

  const timeSaved = traditionalWeeks - venuryWeeks;
  const costSaved = traditionalCost - venuryCost;
  const revenueGain = Math.round(monthlyRevenue * (timeSaved / 4));
  const totalROI = revenueGain + costSaved;

  return (
    <main data-testid="roi-calculator-page">
      <SEOHead
        title="Project ROI Calculator — Venury"
        description="Estimate your potential savings and return on investment when building with Venury versus traditional agencies."
        url="https://venury.tech/resources/roi-calculator"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-20%", right: "-10%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <Link to="/resources" className="inline-flex items-center gap-2 font-mono text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
              <ArrowLeft size={14} /> Resources
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-6">
            <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">
              Interactive Tool
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight"
          >
            Project ROI Calculator
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg text-slate-400 leading-relaxed max-w-xl mt-6"
          >
            Estimate your potential savings and return on investment when building with Venury 
            versus traditional agencies.
          </motion.p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Inputs */}
            <div>
              <ScrollReveal>
                <div className="space-y-8">
                  {/* Project Type */}
                  <div>
                    <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-4">Project Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      {projectTypes.map((type, i) => (
                        <button
                          key={i}
                          data-testid={`project-type-${i}`}
                          onClick={() => setProjectType(i)}
                          className={`p-4 rounded-lg border text-left transition-all duration-200 ${
                            projectType === i
                              ? "border-teal-primary bg-teal-primary/10 text-white"
                              : "border-white/5 text-slate-400 hover:border-white/20"
                          }`}
                        >
                          <span className="text-sm font-semibold block">{type.label}</span>
                          <span className="font-mono text-xs text-slate-600 mt-1 block">{type.baseWeeks} wk base</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Complexity */}
                  <div>
                    <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-4">Complexity Level</label>
                    <div className="grid grid-cols-3 gap-3">
                      {complexityLevels.map((level, i) => (
                        <button
                          key={i}
                          data-testid={`complexity-${i}`}
                          onClick={() => setComplexity(i)}
                          className={`p-4 rounded-lg border text-center transition-all duration-200 ${
                            complexity === i
                              ? "border-teal-primary bg-teal-primary/10 text-white"
                              : "border-white/5 text-slate-400 hover:border-white/20"
                          }`}
                        >
                          <span className="text-sm font-semibold block">{level.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Revenue Input */}
                  <div>
                    <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-4">
                      Expected Monthly Revenue After Launch
                    </label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      <input
                        type="number"
                        data-testid="revenue-input"
                        value={monthlyRevenue}
                        onChange={(e) => setMonthlyRevenue(Number(e.target.value))}
                        className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-teal-primary/50 transition-colors"
                      />
                    </div>
                  </div>

                  <button
                    data-testid="calculate-roi-btn"
                    onClick={() => setShowResults(true)}
                    className="w-full py-4 bg-teal-primary hover:bg-teal-dark text-white font-bold rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <Calculator size={18} />
                    Calculate ROI
                  </button>
                </div>
              </ScrollReveal>
            </div>

            {/* Results */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: showResults ? 1 : 0.3, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="glass-card rounded-lg p-8">
                  <h3 className="font-heading text-xl font-bold text-white mb-8">
                    {showResults ? "Your Estimated ROI" : "Configure your project to see results"}
                  </h3>

                  <div className="space-y-6">
                    {/* Time Comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/10">
                        <span className="font-mono text-xs text-slate-600 block mb-1">Traditional Agency</span>
                        <span className="font-heading text-2xl font-bold text-white">{traditionalWeeks} wk</span>
                        <span className="font-mono text-xs text-slate-600 block">${traditionalCost.toLocaleString()}</span>
                      </div>
                      <div className="p-4 rounded-lg bg-teal-primary/5 border border-teal-primary/10">
                        <span className="font-mono text-xs text-teal-bright block mb-1">With Venury</span>
                        <span className="font-heading text-2xl font-bold text-teal-bright">{venuryWeeks} wk</span>
                        <span className="font-mono text-xs text-teal-bright block">${venuryCost.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="border-t border-white/5 pt-6 space-y-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Clock size={18} className="text-teal-bright" />
                          <span className="text-sm text-slate-400">Time Saved</span>
                        </div>
                        <span className="font-mono text-lg font-bold text-white">{timeSaved} weeks</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <DollarSign size={18} className="text-teal-bright" />
                          <span className="text-sm text-slate-400">Cost Savings</span>
                        </div>
                        <span className="font-mono text-lg font-bold text-white">${costSaved.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TrendingUp size={18} className="text-teal-bright" />
                          <span className="text-sm text-slate-400">Earlier Revenue (est.)</span>
                        </div>
                        <span className="font-mono text-lg font-bold text-white">${revenueGain.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="border-t border-white/5 pt-6">
                      <div className="flex items-center justify-between">
                        <span className="font-heading text-lg font-semibold text-white">Total Estimated ROI</span>
                        <span className="font-mono text-2xl font-bold text-teal-bright">${totalROI.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {showResults && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="mt-8 pt-6 border-t border-white/5"
                    >
                      <Link
                        to="/contact"
                        data-testid="roi-cta"
                        className="w-full py-3 bg-orange-primary hover:bg-orange-dark text-white font-bold rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        Discuss Your Project <ArrowUpRight size={16} />
                      </Link>
                      <p className="text-xs text-slate-600 text-center mt-3">
                        These estimates are based on our historical project data and industry benchmarks.
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
